import { toast } from "react-toastify";
import CrudService from "services/cruds-service";

/**
 * Fetch all entities for search/picker.
 * Per user feedback: no need for token, query, limit.
 * getEntities returns an array with id and name/first_name/last_name.
 */
export async function searchEntities() {
  try {
    const res = await CrudService.getEntitiesList();
    // Assuming API follows JSON:API or similar structure based on project patterns
    return res.data || [];
  } catch (error) {
    console.error("Failed to search entities", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

/**
 * Fetch dropdown options for the onboarding forms.
 */
export async function loadDropdownData() {
  try {
    const data = await CrudService.onboardingDropData();
    return data;
  } catch (error) {
    console.error("Failed to load dropdown data", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

/**
 * Add a new address to an existing entity.
 */
export async function addAddressToEntity(payload) {
  try {
    const res = await CrudService.addAddressToEntity(payload);
    return res;
  } catch (error) {
    console.error("Failed to add address to entity", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

/**
 * Fetch contacts by entity (to get real IDs)
 */
export async function getAllContactsByEntity(entityId) {
  try {
    const res = await CrudService.getAllContactsByEntity(entityId);
    return res;
  } catch (error) {
    console.error("Failed to add address to entity", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

/**
 * Set contact→item access
 */
export async function setContactDeviceAccess(contactId, entityId, accessList) {
  try {
    const res = await CrudService.upsertContactDeviceAccess({
      contact_id: contactId,
      entity_id: entityId,
      payload: accessList,
    });
    return res;
  } catch (error) {
    console.error("Failed to add contact device access", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

// ── 4. Create additional contacts ─────────────────────────────
// Matches: POST /contacts
export async function createAdditionalContact(contact, entity_id) {
  try {
    const payload = {
      data: {
        attributes: {
          first_name: contact.contact_first_name,
          last_name: contact.contact_last_name,
          contactType: { contact_type_id: contact.contact_type_id },
          statusType: { status_type_id: contact.contact_status_type_id || 1 },
          phoneAddresses: contact.phone
            ? [
                {
                  contact_address: contact.phone,
                  description: "Mobile",
                  address_type_id: 1,
                },
              ]
            : [],
          emailAddresses: contact.email
            ? [
                {
                  contact_address: contact.email,
                  description: "Primary Email",
                  address_type_id: 2,
                },
              ]
            : [],
          faxAddresses: [],
          target_entity_id: entity_id
        },
        selectedPhoneId: 0,
        selectedEmailId: 0,
        selectedFaxId: 0,
      },
    };

    const res = await CrudService.createContact(payload);
    return res;
  } catch (error) {
    console.error("Failed to create additional contact", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}

// ── 5. Batch-link contacts to entity ──────────────────────────
export async function batchAddContacts(entityId, contactIds) {
  try {
    const res = await CrudService.linkContactsToEntity(entityId, contactIds);
    return res;
  } catch (error) {
    console.error("Failed to link contacts to entity", error);
    toast.error(error.error ?? error.message);  
    throw error;
  }
}

export async function loadEntityAddressItems(entityId, options) {
  const fetchPage = async (page, pageSize) => {
    const params = new URLSearchParams({
      entity_id: String(entityId),
      page: String(page),
      page_size: String(pageSize),
    });

    if (typeof options?.address_id === "number")
      params.set("address_id", String(options.address_id));
    if (typeof options?.status_type_id === "number")
      params.set("status_type_id", String(options.status_type_id));
    if (options?.search?.trim()) params.set("search", options.search.trim());

    const res = await CrudService.getEntityAddressItemsBy(params.toString());
    return res;
  };

  const pageSize = options?.page_size ?? 200;
  const firstPage = options?.page ?? 1;
  const shouldFetchAll = options?.fetchAll ?? true;

  const first = await fetchPage(firstPage, pageSize);
  if (!shouldFetchAll) return first;

  const firstRows = Array.isArray(first?.data?.data)
    ? first.data.data
    : Array.isArray(first?.data)
    ? first.data
    : Array.isArray(first)
    ? first
    : [];

  let allRows = [...firstRows];
  const total = Number(first?.total ?? first?.data?.total ?? 0);

  if (total > allRows.length) {
    let page = firstPage + 1;
    while (allRows.length < total && page < firstPage + 50) {
      const next = await fetchPage(page, pageSize);
      const nextRows = Array.isArray(next?.data?.data)
        ? next.data.data
        : Array.isArray(next?.data)
        ? next.data
        : Array.isArray(next)
        ? next
        : [];
      if (nextRows.length === 0) break;
      allRows = allRows.concat(nextRows);
      page += 1;
    }
  }

  return { data: allRows, total: total || allRows.length };
}

export async function loadEntityModules(entityId, options) {
  const fetchPage = async (page, pageSize) => {
    const params = new URLSearchParams({
      module_id: String(entityId),
      page: String(page),
      page_size: String(pageSize),
    });

    if (typeof options?.address_id === "number")
      params.set("address_id", String(options.address_id));
    if (typeof options?.status_type_id === "number")
      params.set("status_type_id", String(options.status_type_id));
    if (options?.search?.trim()) params.set("search", options.search.trim());

    const res = await CrudService.getEntityAddressItemsBy(params.toString());
    return res;
  };

  const pageSize = options?.page_size ?? 200;
  const firstPage = options?.page ?? 1;
  const shouldFetchAll = options?.fetchAll ?? true;

  const first = await fetchPage(firstPage, pageSize);
  if (!shouldFetchAll) return first;

  const firstRows = Array.isArray(first?.data?.data)
    ? first.data.data
    : Array.isArray(first?.data)
    ? first.data
    : Array.isArray(first)
    ? first
    : [];

  let allRows = [...firstRows];
  const total = Number(first?.total ?? first?.data?.total ?? 0);

  if (total > allRows.length) {
    let page = firstPage + 1;
    while (allRows.length < total && page < firstPage + 50) {
      const next = await fetchPage(page, pageSize);
      const nextRows = Array.isArray(next?.data?.data)
        ? next.data.data
        : Array.isArray(next?.data)
        ? next.data
        : Array.isArray(next)
        ? next
        : [];
      if (nextRows.length === 0) break;
      allRows = allRows.concat(nextRows);
      page += 1;
    }
  }

  return { data: allRows, total: total || allRows.length };
}

export async function loadShareableProviders() {
  try {
    const response = await CrudService.getProviderShareOptions()
    const providers = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : []

    return providers.filter((provider) => provider.can_share === true)
  } catch (error) {
    console.error('Failed to load provider sharing options', error)
    toast.error(error.error ?? error.message)
    throw error
  }
}

export async function getEntityById(entityId) {
  try {
    const res = await CrudService.getEntity(entityId);
    return res;
  } catch (error) {
    toast.error(error.error ?? error.message);
    console.error("Failed to link contacts to entity", error);
    throw error;
  }
}

export async function createEntityAddressItem(payload) {
  try {
    const res = await CrudService.createEntityAddressItem(payload);
    return res;
  } catch (error) {
    toast.error(error.error ?? error.message);
    console.error("Failed to create entity address item", error);
    throw error;
  }
}

export async function upsertBatchItemAttributesValues(payload) {
  try {
    const res = await CrudService.upsertBatchItemAttributesValues(payload);
    return res;
  } catch (error) {
    toast.error(error.error ?? error.message);
    console.error("Failed to upsert items attributes", error);
    throw error;
  }
}

export async function getAddressesByEntityId(entityId) {
  try {
    const res = await CrudService.getAddressListByEntity(entityId);
    return res?.data ?? [];
  } catch (error) {
    toast.error(error.error ?? error.message);
    console.error("Failed to link contacts to entity", error);
    throw error;
  }
}

export async function loadDevicesTable() {
  try {
    const res = await CrudService.getDevicesListForOnboarding();
    return res?.data ?? [];
  } catch (error) {
    toast.error(error.error ?? error.message);
    console.error("Failed to link contacts to entity", error);
    throw error;
  }
}

export async function loadAlertRuleLookups(deviceId) {
  try {
    const res = await CrudService.loadAlertRuleLookup(deviceId);
    const {
      alertTypes,
      alertSubTypes,
      eventTypes,
      sendToTypes,
      contacts,
      statuses,
    } = res;

    // Resolve active status_type_id for the alert module
    const statusList = statuses || [];
    const activeStatus = statusList.find(
      (s) =>
        (s.StatusType?.status_name ?? s.status_name ?? "").toLowerCase() ===
          "active" ||
        (s.attributes?.StatusType?.status_name ?? "").toLowerCase() ===
          "active",
    );
    const activeStatusTypeId =
      activeStatus?.status_type_id ??
      activeStatus?.attributes?.status_type_id ??
      1;

    return {
      alertTypes: alertTypes,
      subTypes: alertSubTypes,
      eventTypes: eventTypes,
      sendToTypes: sendToTypes,
      entityContacts: contacts,
      activeStatusTypeId,
    };
  } catch (error) {
    console.error("Failed to load alert rule lookups", error);
    toast.error(error.error ?? error.message);
    throw error;
  }
}
